import { Injectable, inject } from '@angular/core';
import { ReplaySubject } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { CmsApiService } from '../api/cms-api.service';
import { UserService } from '../user/user.service';
import { LanguageService } from "../language.service";

export interface GameProvider {
  id: number;
  alias: string;
  imgAlias: string;
  name: string;
  restrictCountry: [];
  inFooter?: boolean;
  inFilter?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class GameProvidersService {
  private _api = inject(CmsApiService);
  private _user = inject(UserService);
  private _lang = inject(LanguageService);


  /**
   * Game provider list observable
   */
  private _list$: ReplaySubject<Array<GameProvider>> = new ReplaySubject<Array<GameProvider>>(1);

  /**
   * Game provider list
   */
  private _list: Array<GameProvider> = [];

  /**
   * Game provider list for filter
   */
  private _filterProvidersList: GameProvider[] = [];

  constructor() {
    this._lang.langChange$.subscribe(() => {
      this.updateList();
    });
  }

  /**
   * Access to provider list observable from outside
   */
  get list$(): ReplaySubject<Array<GameProvider>> {
    return this._list$;
  }

  /**
   * Access to provider list from outside
   */
  get list(): Array<GameProvider> {
    return this._list;
  }

  /**
   * Access to filter provider list from outside
   */
  public get filterProvidersList() {
    return this._filterProvidersList;
  }

  /**
   * Returns provider by id
   *
   * @param id
   */
  getById(id: number): GameProvider {
    return this._list.find(provider => provider.id.toString() === id.toString());
  }

  /**
   * Returns provider by name
   *
   * @param alias
   */
  getByAlias(alias: string): GameProvider {
    alias = alias.toLowerCase().replace(' ', '-');
    return this._list.find(provider => provider.alias.toLowerCase() === alias);
  }

  /**
   * Get provider list from CMS and save to local variables
   */
  updateList() {
    let params = {};

    if (this._user.auth) {
      params = {
        country: this._user.info.country
      };
    }

    this._api.gameProviderList(params).pipe(
      map(response => {
        return (response.data || []).map(provider => ({
          ...provider,
          alias: provider.alias.toLowerCase()
        })).sort((a, b) => {
          if (a.name < b.name) { return -1; }
          if (a.name > b.name) { return 1; }
          return 0;
        });
      }),
      tap(list => {
        this._list = list;
        this._filterProvidersList = list.filter(e => e.inFilter);
        this._list$.next(this._list);
      })
    ).subscribe();
  }
}
